/**
 * aerialComposite — applies the baked Hillaire aerial-perspective froxel
 * volume (AerialPerspectiveLut) to the shaded scene as a post pass:
 *
 *   out = sceneColor · ap.a + ap.rgb        (per froxel sample)
 *
 * The froxel W coordinate is the view DISTANCE (not raw depth): view-space Z
 * from the hardware depth via perspectiveDepthToViewZ, corrected by the
 * per-pixel secant (rays toward screen corners travel farther per unit depth)
 * so the haze is radially consistent, then converted world-units → km and
 * normalized by the volume's max distance. Sky pixels (depth ≈ far plane) are
 * masked out — the sky-view LUT already contains full-path scattering; adding
 * the froxel's would double-count.
 *
 * This closes the "baked but never composited" gap (2026-08-17 shader audit):
 * SkyLutSet bakes the volume behind `enableAerialPerspective`; this node is
 * the consumer, wired via PostProcessingPipeline's `aerial` config.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, mix, clamp,
  texture3D, perspectiveDepthToViewZ,
} from 'three/tsl';
import { depthScalar, skyMask } from '../../depth/skyMask.js';

export interface AerialCompositeConfig {
  /** The baked froxel volume (SkyLutSet.aerial.texture). */
  volume: THREE.Data3DTexture;
  /** Max froxel distance in km — must match the bake (default 32). */
  maxDistanceKm?: number;
  /** World-unit → km conversion (default 0.001: metres). */
  kmPerWorldUnit?: number;
  /** Effect strength 0..1 (default 1). */
  intensity?: number;
  enabled?: boolean;
}

export interface AerialCompositeUniforms {
  near: { value: number };
  far: { value: number };
  tanHalfFovY: { value: number };
  aspect: { value: number };
  maxDistanceKm: { value: number };
  kmPerWorldUnit: { value: number };
  intensity: { value: number };
}

/** CPU mirror of the froxel-W computation (exported for tests). */
export function froxelWCpu(
  viewZ: number, ndcX: number, ndcY: number,
  tanHalfFovY: number, aspect: number,
  kmPerWorldUnit: number, maxDistanceKm: number,
): number {
  const rx = ndcX * tanHalfFovY * aspect;
  const ry = ndcY * tanHalfFovY;
  const secant = Math.sqrt(rx * rx + ry * ry + 1);
  const distKm = -viewZ * secant * kmPerWorldUnit;
  const w = distKm / maxDistanceKm;
  return w <= 0 ? 0 : (w > 1 ? 1 : w); // <=: viewZ 0 yields -0, normalize it
}

export function createAerialCompositeNode(
  input: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  config: AerialCompositeConfig,
): { node: unknown; uniforms: AerialCompositeUniforms } {
  const cam = camera as THREE.PerspectiveCamera;
  const uNear = uniform(cam.near ?? 0.1);
  const uFar = uniform(cam.far ?? 1000);
  const uTanHalf = uniform(Math.tan(((cam.fov ?? 60) * Math.PI) / 360));
  const uAspect = uniform(cam.aspect ?? 1);
  const uMaxKm = uniform(config.maxDistanceKm ?? 32);
  const uKmPerUnit = uniform(config.kmPerWorldUnit ?? 0.001);
  const uIntensity = uniform(config.intensity ?? 1);

  const build = Fn(() => {
    const c = vec4(input as never);
    // Scalar depth (the `.r` reduction — same convention as GodRays/clouds).
    const depth = depthScalar(depthNode) as never;
    const viewZ = perspectiveDepthToViewZ(depth, uNear, uFar); // negative metres
    const ndc = uv().mul(2).sub(vec2(1, 1));
    const ray = vec3(ndc.x.mul(uTanHalf).mul(uAspect), ndc.y.mul(uTanHalf), float(1));
    const distKm = viewZ.negate().mul(ray.length()).mul(uKmPerUnit);
    const w = clamp(distKm.div(uMaxKm), float(0), float(1));
    const ap = texture3D(config.volume, vec3(uv().x, uv().y, w));
    const trans = mix(float(1), ap.a, uIntensity);
    const inscatter = ap.rgb.mul(uIntensity);
    const composited = c.rgb.mul(trans).add(inscatter);
    // Sky pixels keep the sky-view result (it already carries full-path
    // scattering; adding the froxel's would double-count). WHY the helper: the
    // old `step(0.999, depth)` is z ≈ near/0.001 = 50 m on this camera, so it
    // masked the haze off everything BEYOND 50 m — the effect inverted. See
    // render/depth/skyMask.ts.
    return vec4(mix(composited, c.rgb, skyMask(depth)), c.a);
  });

  return {
    node: build(),
    uniforms: {
      near: uNear as unknown as { value: number },
      far: uFar as unknown as { value: number },
      tanHalfFovY: uTanHalf as unknown as { value: number },
      aspect: uAspect as unknown as { value: number },
      maxDistanceKm: uMaxKm as unknown as { value: number },
      kmPerWorldUnit: uKmPerUnit as unknown as { value: number },
      intensity: uIntensity as unknown as { value: number },
    },
  };
}
